'use client';
import { useId } from 'react';
import { Skeleton } from '../../components/ui/shadcn/skeleton';
import { Button } from '../../components/ui/Button';
import { useTranslation } from '../../lib/i18n';
import type { Profile } from '../../lib/types';

/** What the quote above a reply shows. The caller resolves the target (the loaded turn, else the single
 *  message read) and the jump; this block only draws the outcome. `unavailable` is a target that is gone
 *  (404, or the jump could not reach it); `failed` is a read that did not answer, which may be retried. */
export type ReplyQuoteState =
  | { status: 'loading' }
  | { status: 'unavailable' }
  | { status: 'failed'; retry: () => void }
  | { status: 'ready'; author: { kind: 'agent' } | { kind: 'user'; profile: Profile }; text: string };

const SNIPPET_CHARS = 160;

/** The quoted text as one line: whitespace runs collapse, and past 160 code points it ends in an ellipsis. */
export function quoteSnippet(text: string): string {
  const line = text.split(/\s+/).filter(Boolean).join(' ');
  const chars = [...line];
  return chars.length > SNIPPET_CHARS ? `${chars.slice(0, SNIPPET_CHARS).join('')}…` : line;
}

/** The quote above an assistant text that replies to one earlier message: a quiet reference row behind a
 *  thin neutral rule, the quoted author's name (Chetty for the agent) and the snippet truncated to one line,
 *  all in the muted small text. The row is the jump to the original; it is disabled while the target
 *  resolves and once it is known to be gone.
 *
 *  Both controls are the shared `Button` on its `inline` step — the one-line reference geometry that keeps a
 *  centred 44px hit area — so the row states no height, padding or focus ring of its own. */
export function ReplyQuote({ quote, onJump }: { quote: ReplyQuoteState; onJump: () => void }) {
  const { t } = useTranslation();
  const textId = useId();
  if (quote.status === 'failed') {
    return (
      <div data-testid="chat-reply-quote" data-status="failed" role="group" aria-label={t.chat.replyTo} className="my-1 min-w-0 border-l-2 border-border pl-2">
        <Button type="button" variant="ghost" size="inline" onClick={quote.retry} aria-label={`${t.chat.replyTo}: ${t.common.retry}`}>{t.common.retry}</Button>
      </div>
    );
  }
  const name = quote.status !== 'ready' ? null
    : quote.author.kind === 'agent' ? t.chat.roleElowen
    : quote.author.profile.name.trim() || quote.author.profile.username;
  return (
    <div data-testid="chat-reply-quote" data-status={quote.status} role="group" aria-label={t.chat.replyTo} className="my-1 min-w-0 border-l-2 border-border pl-2">
      <Button
        type="button"
        variant="ghost"
        size="inline"
        disabled={quote.status !== 'ready'}
        aria-busy={quote.status === 'loading' ? true : undefined}
        aria-label={t.chat.jumpToMessage}
        aria-describedby={quote.status === 'loading' ? undefined : textId}
        onClick={onJump}
        className="w-fit max-w-full min-w-0 text-left"
      >
        {quote.status === 'loading' ? <Skeleton className="h-[1lh] w-48 max-w-full" /> : null}
        {quote.status === 'unavailable' ? <span id={textId} className="min-w-0 truncate italic">{t.chat.messageUnavailable}</span> : null}
        {quote.status === 'ready' ? (
          <span id={textId} className="flex min-w-0 items-baseline gap-1">
            <span className="shrink-0">{name}:</span>{' '}
            <span className="min-w-0 truncate">{quoteSnippet(quote.text)}</span>
          </span>
        ) : null}
      </Button>
    </div>
  );
}
